import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createPageContainer } from "../../components/ui/page-container";
import { createSkipLink } from "../../components/ui/skip-link";
import { fetchQuizzesList, postCreateDraftQuiz } from "../../lib/api-quizzes";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";

const PAGE_SIZE = 20;

export interface RenderMyQuizzesOptions {
  signal: AbortSignal;
  locale: SupportedLocale;
}

interface QuizListItem {
  quiz_id: string;
  title: string;
  status: string;
  created_at: string;
  generation_pipeline_status: string;
}

/** When false, the row shows lifecycle only (omit idle or succeeded pipeline noise). */
function listShowsGenerationPhase(raw: string | undefined): boolean {
  const p = (raw ?? "idle").trim().toLowerCase();
  return p !== "" && p !== "idle" && p !== "succeeded";
}

function quizLifecycleLabel(t: Translate, status: string): string {
  const s = status.trim().toLowerCase();
  if (s === "draft") {
    return t("quiz.my_quizzes.quiz_status_draft");
  }
  if (s === "published") {
    return t("quiz.my_quizzes.quiz_status_published");
  }
  return t("quiz.my_quizzes.quiz_status_other", { status });
}

function generationPhaseLabelForList(t: Translate, phase: string): string {
  const p = phase.trim().toLowerCase();
  if (p === "running") {
    return t("quiz.my_quizzes.generation_phase_running");
  }
  if (p === "failed") {
    return t("quiz.my_quizzes.generation_phase_failed");
  }
  if (p === "queued") {
    return t("quiz.my_quizzes.generation_phase_queued");
  }
  return t("quiz.my_quizzes.generation_phase_other", { phase });
}

function formatMyQuizSummaryLine(t: Translate, row: QuizListItem): string {
  const lifecycle = quizLifecycleLabel(t, row.status);
  const pipe = row.generation_pipeline_status;
  if (!listShowsGenerationPhase(pipe)) {
    return lifecycle;
  }
  return t("quiz.my_quizzes.summary_with_pipeline", {
    lifecycle,
    pipeline: generationPhaseLabelForList(t, pipe),
  });
}

/** Published quizzes open read-only detail; everything else opens the setup wizard. */
function quizListRowHref(quizId: string, status: string): string {
  if (status.trim().toLowerCase() === "published") {
    return `/quizzes/view/${encodeURIComponent(quizId)}`;
  }
  return `/quizzes/wizard/${encodeURIComponent(quizId)}`;
}

function formatQuizCreatedAt(iso: string, locale: SupportedLocale): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return iso;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ms));
}

function isListPayload(
  data: unknown,
): data is { items: QuizListItem[]; limit: number; has_more?: boolean } {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const rec = data as Record<string, unknown>;
  if (!Array.isArray(rec.items)) {
    return false;
  }
  if (typeof rec.limit !== "number") {
    return false;
  }
  if ("has_more" in rec && rec.has_more !== undefined) {
    if (typeof rec.has_more !== "boolean") {
      return false;
    }
  }
  for (const it of rec.items) {
    if (typeof it !== "object" || it === null) {
      return false;
    }
    const row = it as Record<string, unknown>;
    if (
      typeof row.quiz_id !== "string" ||
      typeof row.title !== "string" ||
      typeof row.status !== "string" ||
      typeof row.created_at !== "string" ||
      typeof row.generation_pipeline_status !== "string"
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Renders **My quizzes** (**GET** **`/api/v1/quizzes`**, **POST** **`/api/v1/quizzes`**, **wizard** **`/quizzes/wizard/...`**) for signed-in organizers.
 *
 * @param mount - Container cleared and filled by this view.
 * @param t - Localized string resolver.
 * @param options - Lifecycle controls (abort signal, locale for dates).
 */
export async function renderMyQuizzes(
  mount: HTMLElement,
  t: Translate,
  options: RenderMyQuizzesOptions,
): Promise<void> {
  mount.replaceChildren();

  const shell = document.createElement("div");
  shell.className = "home-shell";

  shell.appendChild(
    createSkipLink(t("quiz.home.skip_to_main"), "my-quizzes-main"),
  );

  const page = createPageContainer();
  shell.appendChild(page);

  const { header, authSlot } = createStandardSiteAppHeader(t, options.locale);
  header.classList.add("my-quizzes__header");
  page.appendChild(header);

  void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
    signal: options.signal,
  });

  const main = document.createElement("main");
  main.id = "my-quizzes-main";
  main.className = "my-quizzes__main";
  main.tabIndex = -1;

  const h1 = document.createElement("h1");
  h1.className = "qz-heading qz-heading--1 my-quizzes__title";
  h1.textContent = t("quiz.my_quizzes.title");
  main.appendChild(h1);

  const createWrap = document.createElement("div");
  createWrap.className = "my-quizzes__create";
  const createBtn = document.createElement("button");
  createBtn.type = "button";
  createBtn.className = "qz-btn qz-btn--primary my-quizzes__create-btn";
  createBtn.textContent = t("quiz.my_quizzes.create_submit");
  createWrap.appendChild(createBtn);
  main.appendChild(createWrap);

  const status = document.createElement("p");
  status.className = "my-quizzes__status";
  status.setAttribute("aria-live", "polite");
  status.textContent = t("quiz.my_quizzes.loading");
  main.appendChild(status);

  const listRegion = document.createElement("div");
  listRegion.className = "my-quizzes__list";
  main.appendChild(listRegion);

  page.appendChild(main);
  mount.appendChild(shell);

  let accumulatedItems: QuizListItem[] = [];
  let hasMorePages = false;
  let loadingMore = false;
  let listObserver: IntersectionObserver | null = null;

  function disconnectListObserver(): void {
    listObserver?.disconnect();
    listObserver = null;
  }

  options.signal.addEventListener(
    "abort",
    () => {
      disconnectListObserver();
    },
    { once: true },
  );

  function buildQuizRow(row: QuizListItem): HTMLLIElement {
    const li = document.createElement("li");
    li.className = "my-quizzes__quiz-row";

    const nav = document.createElement("a");
    nav.className = "my-quizzes__row-link";
    nav.href = quizListRowHref(row.quiz_id, row.status);

    const createdLine = document.createElement("time");
    createdLine.className = "my-quizzes__quiz-created";
    createdLine.dateTime = row.created_at;
    createdLine.textContent = t("quiz.my_quizzes.created_line", {
      date: formatQuizCreatedAt(row.created_at, options.locale),
    });
    nav.appendChild(createdLine);

    const titleReadonly = document.createElement("p");
    titleReadonly.className = "my-quizzes__quiz-title-readonly";
    titleReadonly.textContent =
      row.title.trim() !== ""
        ? row.title
        : t("quiz.my_quizzes.untitled_placeholder");
    nav.appendChild(titleReadonly);

    const meta = document.createElement("p");
    meta.className = "my-quizzes__quiz-meta";
    meta.textContent = formatMyQuizSummaryLine(t, row);
    nav.appendChild(meta);

    li.appendChild(nav);
    return li;
  }

  async function loadMore(
    ul: HTMLUListElement,
    footerLoading: HTMLParagraphElement,
    sentinel: HTMLDivElement,
  ): Promise<void> {
    if (!hasMorePages || loadingMore || accumulatedItems.length === 0) {
      return;
    }
    const last = accumulatedItems[accumulatedItems.length - 1];
    loadingMore = true;
    footerLoading.hidden = false;
    try {
      const res = await fetchQuizzesList(
        { signal: options.signal },
        {
          limit: PAGE_SIZE,
          cursorCreatedAt: last.created_at,
          cursorQuizId: last.quiz_id,
        },
      );
      if (options.signal.aborted) {
        return;
      }
      if (!res.ok) {
        status.textContent = t("quiz.my_quizzes.load_failed");
        return;
      }
      const text = await res.text();
      if (options.signal.aborted) {
        return;
      }
      let data: unknown;
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        status.textContent = t("quiz.my_quizzes.load_failed");
        return;
      }
      if (!isListPayload(data)) {
        status.textContent = t("quiz.my_quizzes.load_failed");
        return;
      }
      status.textContent = "";
      for (const row of data.items) {
        accumulatedItems.push(row);
        ul.appendChild(buildQuizRow(row));
      }
      hasMorePages =
        typeof data.has_more === "boolean"
          ? data.has_more
          : data.items.length >= PAGE_SIZE;
      if (!hasMorePages) {
        disconnectListObserver();
        sentinel.remove();
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      status.textContent = t("quiz.my_quizzes.load_failed");
    } finally {
      loadingMore = false;
      footerLoading.hidden = true;
    }
  }

  function attachInfiniteScroll(
    ul: HTMLUListElement,
    footerLoading: HTMLParagraphElement,
    sentinel: HTMLDivElement,
  ): void {
    disconnectListObserver();
    if (!hasMorePages) {
      sentinel.remove();
      return;
    }
    listObserver = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (hit) {
          void loadMore(ul, footerLoading, sentinel);
        }
      },
      { root: null, rootMargin: "120px", threshold: 0 },
    );
    listObserver.observe(sentinel);
  }

  async function loadInitial(): Promise<void> {
    disconnectListObserver();
    accumulatedItems = [];
    hasMorePages = false;
    loadingMore = false;
    main.querySelector(".my-quizzes__sign-in-callout")?.remove();
    createWrap.hidden = false;
    status.textContent = t("quiz.my_quizzes.loading");
    listRegion.replaceChildren();

    try {
      const res = await fetchQuizzesList(
        { signal: options.signal },
        { limit: PAGE_SIZE },
      );
      if (options.signal.aborted) {
        return;
      }
      if (res.status === 404) {
        status.textContent = t("quiz.my_quizzes.feature_off");
        listRegion.replaceChildren();
        createWrap.hidden = true;
        return;
      }
      if (res.status === 401) {
        status.textContent = t("quiz.my_quizzes.sign_in_required");
        listRegion.replaceChildren();
        createWrap.hidden = true;
        const gate = document.createElement("p");
        gate.className = "my-quizzes__sign-in-callout";
        const signIn = document.createElement("a");
        signIn.href = "/organizer/sign-in";
        signIn.className = "qz-btn qz-btn--primary";
        signIn.textContent = t("quiz.home.header_sign_in");
        gate.appendChild(signIn);
        main.appendChild(gate);
        return;
      }
      if (!res.ok) {
        status.textContent = t("quiz.my_quizzes.load_failed");
        return;
      }
      const text = await res.text();
      if (options.signal.aborted) {
        return;
      }
      let data: unknown;
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        status.textContent = t("quiz.my_quizzes.load_failed");
        return;
      }
      if (!isListPayload(data)) {
        status.textContent = t("quiz.my_quizzes.load_failed");
        return;
      }

      accumulatedItems = data.items;
      hasMorePages =
        typeof data.has_more === "boolean"
          ? data.has_more
          : data.items.length >= PAGE_SIZE;

      status.textContent = "";
      if (accumulatedItems.length === 0) {
        const empty = document.createElement("p");
        empty.className = "my-quizzes__empty";
        empty.textContent = t("quiz.my_quizzes.empty");
        listRegion.appendChild(empty);
        return;
      }

      const ul = document.createElement("ul");
      ul.className = "my-quizzes__quiz-list";
      for (const row of accumulatedItems) {
        ul.appendChild(buildQuizRow(row));
      }

      const footer = document.createElement("div");
      footer.className = "my-quizzes__infinite-footer";
      const footerLoading = document.createElement("p");
      footerLoading.className = "my-quizzes__loading-more";
      footerLoading.hidden = true;
      footerLoading.textContent = t("quiz.my_quizzes.loading_more");

      const sentinel = document.createElement("div");
      sentinel.className = "my-quizzes__scroll-sentinel";
      sentinel.setAttribute("aria-hidden", "true");

      footer.append(footerLoading, sentinel);
      listRegion.append(ul, footer);
      attachInfiniteScroll(ul, footerLoading, sentinel);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      status.textContent = t("quiz.my_quizzes.load_failed");
    }
  }

  createBtn.addEventListener("click", () => {
    void (async () => {
      createBtn.disabled = true;
      try {
        const res = await postCreateDraftQuiz({ signal: options.signal });
        if (options.signal.aborted) {
          return;
        }
        if (res.status === 404) {
          status.textContent = t("quiz.my_quizzes.feature_off");
          createBtn.disabled = false;
          return;
        }
        if (!res.ok) {
          status.textContent = t("quiz.my_quizzes.create_failed");
          createBtn.disabled = false;
          return;
        }
        let body: unknown;
        try {
          body = (await res.json()) as unknown;
        } catch {
          status.textContent = t("quiz.my_quizzes.create_failed");
          createBtn.disabled = false;
          return;
        }
        const quizId =
          typeof body === "object" &&
          body !== null &&
          typeof (body as { quiz_id?: unknown }).quiz_id === "string"
            ? (body as { quiz_id: string }).quiz_id
            : null;
        if (!quizId) {
          status.textContent = t("quiz.my_quizzes.create_failed");
          createBtn.disabled = false;
          return;
        }
        window.location.assign(`/quizzes/wizard/${encodeURIComponent(quizId)}`);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        status.textContent = t("quiz.my_quizzes.create_failed");
      } finally {
        createBtn.disabled = false;
      }
    })();
  });

  try {
    await loadInitial();
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    status.textContent = t("quiz.my_quizzes.load_failed");
  }
}
